import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import InputAdornment from "@mui/material/InputAdornment";

import TextField from "features/components/TextField";
import Typography from "features/components/Typography";
import FilterDescriptionTable from "features/ui/Filters/other/FilterDescriptionTable";
import SearchIcon from "features/ui/Icons/SearchIcon";

interface Props {
  attribute?: string;
  title?: string;
  values: string[];
  showDescriptionsTable?: boolean;
}

const ValuesSearchList = ({
  attribute,
  title,
  values,
  showDescriptionsTable,
}: Props) => {
  const { t } = useTranslation("common");
  const [searchString, setSearchString] = useState("");

  const data = useMemo(() => {
    if (!searchString) return values.sort();

    const lowerCasedSearchString = searchString.toLowerCase();

    return values
      .filter((value) =>
        value.toString().toLowerCase().includes(lowerCasedSearchString)
      )
      .sort();
  }, [searchString, values]);

  const descriptionsTable = attribute && (
    <FilterDescriptionTable
      fieldName={attribute}
      values={values}
      searchString={searchString}
      key={attribute}
    />
  );

  const listOfValues = (
    <ul className="flex flex-col gap-2 mt-4 pl-4 list-disc">
      {data.map((value, i) => (
        <li key={i}>{value}</li>
      ))}
    </ul>
  );

  const noResultsMessageVisible = data.length === 0 && !showDescriptionsTable;
  const toDisplay = showDescriptionsTable ? descriptionsTable : listOfValues;

  return (
    <div className="p-5 space-y-2" key={title}>
      <div className="flex justify-between mb-3">
        <Typography variant="h5" className="flex space-x-3 items-center">
          {title}
        </Typography>
        <TextField
          onChange={({ target: { value } }) => {
            setSearchString(value);
          }}
          type="text"
          size="small"
          placeholder={t("values_search_list.search_placeholder", { title })}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
          fullWidth={false}
        />
      </div>

      <div className="max-h-[40rem] overflow-y-auto">
        {noResultsMessageVisible &&
          t("values_search_list.no_results", { title })}
        {!noResultsMessageVisible && toDisplay}
      </div>
    </div>
  );
};

export default ValuesSearchList;
